import { type ReactNode, useEffect, useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@/lib/utils';

import { ImageReveal } from './image-reveal';

export interface MediaFrameProps {
  /** Image URL. Used directly, or as the video poster when `video` is set. */
  image: string;
  /** Optional video URL — plays muted/looped on view; poster = `image`. */
  video?: string;
  alt: string;
  className?: string;
  /** Tailwind aspect utility, e.g. 'aspect-video' (default), 'aspect-[4/5]'. */
  aspect?: string;
  /** Navy overlay opacity 0–100 (use 55–75 when white text sits on top). */
  overlay?: number;
  /** Scroll curtain-wipe reveal (default true). */
  reveal?: boolean;
  rounded?: string;
  /** CSS object-position for the media, e.g. '50% 20%' or 'top' (default 'center'). */
  position?: string;
  /** Content layered over the media (captions, labels). */
  children?: ReactNode;
}

/**
 * The standard way to place a visual on the IBAS site: an image or (muted,
 * looping, on-view) video with a built-in scroll reveal, brand navy overlay and
 * required alt text. Video autoplays only while in view and is fully paused —
 * showing just the poster — under reduced motion (brand "no disruptive
 * autoplay" rule). Demo/stock media is acceptable pending IBAS-owned assets.
 */
export function MediaFrame({
  image,
  video,
  alt,
  className,
  aspect = 'aspect-video',
  overlay = 0,
  reveal = true,
  rounded = 'rounded-2xl',
  position = 'center',
  children,
}: MediaFrameProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduced = useReducedMotion();
  const showVideo = Boolean(video) && !reduced;
  // Rect-based in/out-of-view (not IntersectionObserver, which never fires
  // inside a `preserve-3d` ancestor — e.g. a TiltCard — leaving the video stuck
  // on its poster). `once:false` so it plays when scrolled into view and pauses
  // when it leaves.
  const inView = useInView(videoRef, { once: false, threshold: 0.95, disabled: !showVideo });

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !showVideo) return;

    if (inView) {
      // A metadata-preloaded video may not be ready the instant it enters view;
      // retry on `canplay` so the first in-view play always lands.
      const tryPlay = () => el.play().catch(() => {});
      tryPlay();
      el.addEventListener('canplay', tryPlay);
      return () => el.removeEventListener('canplay', tryPlay);
    }

    el.pause();
  }, [inView, showVideo]);

  const media = (
    <div className={cn('relative h-full w-full', rounded, 'overflow-hidden')}>
      {showVideo ? (
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          style={{ objectPosition: position }}
          poster={image}
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={alt}
        >
          <source src={video} type="video/mp4" />
        </video>
      ) : (
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
          style={{ objectPosition: position }}
        />
      )}

      {overlay > 0 && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-brand-navy"
          style={{ opacity: overlay / 100 }}
        />
      )}

      {children && <div className="absolute inset-0">{children}</div>}
    </div>
  );

  const frame = <div className={cn('relative', aspect, rounded, className)}>{media}</div>;

  return reveal ? (
    <ImageReveal className={cn(rounded, className)}>
      <div className={cn('relative', aspect)}>{media}</div>
    </ImageReveal>
  ) : (
    frame
  );
}
